import React, { useState, useEffect } from 'react';
import type { DragEndEvent } from '@dnd-kit/core';
import { DndContext } from '@dnd-kit/core';
import { Table } from 'antd';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MenuOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table/interface';
import { TableWrapper } from 'components/molecules';

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
    'data-row-key': string;
}

const Row = ({ children, ...props }: RowProps) => {
const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
} = useSortable({
    id: props['data-row-key'],
});

const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1 })?.replace(
    /translate3d\(([^,]+),/,
    'translate3d(0,',
    ),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 99 } : {}),
};

return (
    <tr {...props} ref={setNodeRef} style={style} {...attributes}>
    {React.Children.map(children, (child) => {
        if ((child as React.ReactElement).key === 'sort') {
        return React.cloneElement(child as React.ReactElement, {
            children: (
            <MenuOutlined
                ref={setActivatorNodeRef}
                style={{ touchAction: 'none', cursor: 'move' }}
                {...listeners}
            />
            ),
        });
        }
        return child;
    })}
    </tr>
);
};

  
interface SortableTableProps {
    dataList: any[];
    columns: ColumnsType<any>;
    rowSelected?: boolean;
    onSort: (active: string | number, over: string | number, activeObj?: any)  => void;
    onClick: (record: any) => void;
}

const SortableTable = ({ dataList, columns, rowSelected = false, onSort, onClick }: SortableTableProps): React.ReactElement => {
  /* ======================================================
   * states
   * ====================================================== */
  const [datas, setDatas] = useState<any[]>();
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  /* ======================================================
   * event
   * ====================================================== */
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setDatas((previous) => {
        if (previous) {
          const activeIndex = previous.findIndex((i) => i.key === active.id);
          const overIndex = previous.findIndex((i) => i.key === over?.id);

          onSort(previous[activeIndex].key, previous[overIndex].key, previous[activeIndex]);

          return arrayMove(previous, activeIndex, overIndex);
        } else {
          return [];
        }
      });
    }
  };

  const onRowClick = (record, index) => {
    return {
      onClick: () => {
        setActiveIndex(index);
        onClick(record);
      },
    };
  };  
  /* ======================================================
   * effects
   * ====================================================== */
  useEffect(() => {
    setDatas(dataList);
  }, [dataList]);

  /* ======================================================
   * render items
   * ====================================================== */
  return (
    <DndContext onDragEnd={onDragEnd}>
        <SortableContext
        // rowKey array
        items={datas?datas.map((item) => item.key):[]}
        strategy={verticalListSortingStrategy}
        >
          <TableWrapper isSearch={false}>
            <Table
              components={{
                body: {
                  row: Row,
                },
              }}
              dataSource={datas}
              columns={columns}
              pagination={false}
              onRow={onRowClick}
              rowClassName={(record, index) => {return rowSelected && index === activeIndex?'actived_table_row':''}}
              style={{ width: '100%', overflowX: 'auto', minWidth: 600 }}
              className="columnCenterFixTable"
            />          
          </TableWrapper>  
        </SortableContext>        
    </DndContext>
  );
};

export default SortableTable;
